import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  apiClient,
  refreshSession,
  setAccessToken,
  setAuthInvalidatedListener,
  setAuthRotationListener,
} from "../../lib/api-client";
import { generatePKCEPair } from "../../lib/pkce";
import type { AuthResponse, User } from "../../types/auth";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (name: string, email: string, password: string) => Promise<void>;
  loginWithVK: () => void;
  loginWithGoogle: () => void;
  handleVKCallback: (code: string, deviceId: string) => Promise<void>;
  handleGoogleCallback: (code: string, state: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const VK_AUTHORIZE_URL = "https://id.vk.ru/authorize";
const VK_CLIENT_ID = import.meta.env.VITE_VK_CLIENT_ID;
const GOOGLE_AUTHORIZE_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const SESSION_KEY_VERIFIER = "vk_pkce_verifier";
const SESSION_KEY_DEVICE_ID = "vk_device_id";
const SESSION_KEY_GOOGLE_VERIFIER = "google_pkce_verifier";
const SESSION_KEY_GOOGLE_STATE = "google_oauth_state";
const SESSION_KEY_PROVIDER = "oauth_provider";
const AUTH_HINT_KEY = "fg_auth_hint";

function setAuthHint() {
  localStorage.setItem(AUTH_HINT_KEY, "1");
}

function hasAuthHint(): boolean {
  return localStorage.getItem(AUTH_HINT_KEY) === "1";
}

function clearAuthHint() {
  localStorage.removeItem(AUTH_HINT_KEY);
}

function clearOAuthSession() {
  sessionStorage.removeItem(SESSION_KEY_VERIFIER);
  sessionStorage.removeItem(SESSION_KEY_DEVICE_ID);
  sessionStorage.removeItem(SESSION_KEY_GOOGLE_VERIFIER);
  sessionStorage.removeItem(SESSION_KEY_GOOGLE_STATE);
  sessionStorage.removeItem(SESSION_KEY_PROVIDER);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const scheduleRefresh = useCallback((expiresIn: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    const delay = Math.max(expiresIn * 800, 60_000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const result = await apiClient.post<{ data: AuthResponse }>(
          "/auth/refresh"
        );
        setAccessToken(result.data.access_token);
        setUser(result.data.user);
        setAuthHint();
        scheduleRefresh(result.data.expires_in);
      } catch {
        setUser(null);
        setAccessToken(null);
        clearAuthHint();
        navigate("/login");
      }
    }, delay);
  }, [navigate]);

  const refreshToken = useCallback(async () => {
    const result = await refreshSession();
    setAccessToken(result.access_token);
    setUser(result.user);
    setAuthHint();
    scheduleRefresh(result.expires_in);
  }, [scheduleRefresh]);

  const handleVKCallback = useCallback(
    async (code: string, deviceId: string) => {
      const verifier = sessionStorage.getItem(SESSION_KEY_VERIFIER);
      if (!verifier) {
        throw new Error("PKCE verifier not found. Please try logging in again.");
      }

      const result = await apiClient.post<{ data: AuthResponse }>(
        "/auth/vk/callback",
        {
          code,
          code_verifier: verifier,
          device_id: deviceId,
        }
      );

      clearOAuthSession();

      setAccessToken(result.data.access_token);
      setUser(result.data.user);
      setAuthHint();
      scheduleRefresh(result.data.expires_in);
    },
    [scheduleRefresh]
  );

  const handleGoogleCallback = useCallback(
    async (code: string, state: string) => {
      const verifier = sessionStorage.getItem(SESSION_KEY_GOOGLE_VERIFIER);
      const expectedState = sessionStorage.getItem(SESSION_KEY_GOOGLE_STATE);

      if (!verifier || !expectedState) {
        throw new Error("OAuth session not found. Please try logging in again.");
      }
      if (state !== expectedState) {
        throw new Error("Invalid state. Please try logging in again.");
      }

      const result = await apiClient.post<{ data: AuthResponse }>(
        "/auth/google/callback",
        {
          code,
          code_verifier: verifier,
        }
      );

      clearOAuthSession();

      setAccessToken(result.data.access_token);
      setUser(result.data.user);
      setAuthHint();
      scheduleRefresh(result.data.expires_in);
    },
    [scheduleRefresh]
  );

  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      const result = await apiClient.post<{ data: AuthResponse }>(
        "/auth/login",
        { email, password }
      );
      setAccessToken(result.data.access_token);
      setUser(result.data.user);
      setAuthHint();
      scheduleRefresh(result.data.expires_in);
    },
    [scheduleRefresh]
  );

  const registerWithEmail = useCallback(
    async (name: string, email: string, password: string) => {
      const result = await apiClient.post<{ data: AuthResponse }>(
        "/auth/register",
        { name, email, password }
      );
      setAccessToken(result.data.access_token);
      setUser(result.data.user);
      setAuthHint();
      scheduleRefresh(result.data.expires_in);
    },
    [scheduleRefresh]
  );

  const loginWithVK = useCallback(async () => {
    if (import.meta.env.DEV) {
      try {
        const result = await apiClient.post<{ data: AuthResponse }>(
          "/auth/dev/login",
          { name: "Dev User" }
        );
        setAccessToken(result.data.access_token);
        setUser(result.data.user);
        setAuthHint();
        scheduleRefresh(result.data.expires_in);
        navigate("/", { replace: true });
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : "Dev login failed");
      }
      return;
    }

    const clientId = VK_CLIENT_ID?.trim();
    if (!clientId) {
      setAuthError("Вход через VK ID сейчас недоступен");
      return;
    }

    const { verifier, challenge } = await generatePKCEPair();
    const deviceId = crypto.randomUUID();

    sessionStorage.setItem(SESSION_KEY_VERIFIER, verifier);
    sessionStorage.setItem(SESSION_KEY_DEVICE_ID, deviceId);
    sessionStorage.setItem(SESSION_KEY_PROVIDER, "vk");

    const redirectUri = `${window.location.origin}/login`;
    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId,
      redirect_uri: redirectUri,
      code_challenge: challenge,
      code_challenge_method: "S256",
      scope: "email phone vkid.personal_info",
      state: deviceId,
    });

    window.location.href = `${VK_AUTHORIZE_URL}?${params.toString()}`;
  }, [scheduleRefresh, navigate]);

  const loginWithGoogle = useCallback(async () => {
    if (import.meta.env.DEV) {
      try {
        const result = await apiClient.post<{ data: AuthResponse }>(
          "/auth/dev/login",
          { name: "Dev Google User" }
        );
        setAccessToken(result.data.access_token);
        setUser(result.data.user);
        setAuthHint();
        scheduleRefresh(result.data.expires_in);
        navigate("/", { replace: true });
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : "Dev login failed");
      }
      return;
    }

    const clientId = GOOGLE_CLIENT_ID?.trim();
    if (!clientId) {
      setAuthError("Вход через Google сейчас недоступен");
      return;
    }

    const { verifier, challenge } = await generatePKCEPair();
    const state = crypto.randomUUID();

    sessionStorage.setItem(SESSION_KEY_GOOGLE_VERIFIER, verifier);
    sessionStorage.setItem(SESSION_KEY_GOOGLE_STATE, state);
    sessionStorage.setItem(SESSION_KEY_PROVIDER, "google");

    const googleRedirectUri = `${window.location.origin}/login`;
    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId,
      redirect_uri: googleRedirectUri,
      code_challenge: challenge,
      code_challenge_method: "S256",
      scope: "openid email profile",
      state,
      access_type: "offline",
      prompt: "consent",
    });

    window.location.href = `${GOOGLE_AUTHORIZE_URL}?${params.toString()}`;
  }, [scheduleRefresh, navigate]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch {
      // ignore errors on logout
    }
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    setAccessToken(null);
    setUser(null);
    clearAuthHint();
    clearOAuthSession();
    navigate("/login");
  }, [navigate]);

  // FG-90: wire the api-client's rotation/invalidation listeners into React
  // state. Triggered when the interceptor transparently refreshes after a 401
  // or a 403 `family_context_required`, and also after membership commands
  // ship a fresh access token via notifyAuthRotation in api-family.ts. Without
  // this wiring the new access token sits in the api-client closure but React's
  // auth state goes stale.
  useEffect(() => {
    const unsubRotation = setAuthRotationListener((event) => {
      if (event.user) {
        setUser(event.user);
      }
      setAuthHint();
      scheduleRefresh(event.expires_in);
    });
    const unsubInvalidated = setAuthInvalidatedListener(() => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      setAccessToken(null);
      setUser(null);
      clearAuthHint();
      navigate("/login");
    });
    return () => {
      unsubRotation();
      unsubInvalidated();
    };
  }, [scheduleRefresh, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const provider = sessionStorage.getItem(SESSION_KEY_PROVIDER);

    if (code && state) {
      const handler =
        provider === "google"
          ? handleGoogleCallback(code, state)
          : handleVKCallback(code, state);

      setIsLoading(true);
      handler
        .then(() => {
          window.history.replaceState({}, "", window.location.pathname);
          navigate("/", { replace: true });
        })
        .catch((err: unknown) => {
          setUser(null);
          setAccessToken(null);
          setAuthError(err instanceof Error ? err.message : "Ошибка авторизации");
          window.history.replaceState({}, "", window.location.pathname);
        })
        .finally(() => setIsLoading(false));
      return;
    }

    if (!hasAuthHint()) {
      setIsLoading(false);
      return;
    }

    const tryRestore = async () => {
      try {
        await refreshToken();
      } catch {
        setUser(null);
        setAccessToken(null);
        clearAuthHint();
      } finally {
        setIsLoading(false);
      }
    };
    tryRestore();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      authError,
      loginWithEmail,
      registerWithEmail,
      loginWithVK,
      loginWithGoogle,
      handleVKCallback,
      handleGoogleCallback,
      logout,
      refreshToken,
    }),
    [
      user,
      isLoading,
      authError,
      loginWithEmail,
      registerWithEmail,
      loginWithVK,
      loginWithGoogle,
      handleVKCallback,
      handleGoogleCallback,
      logout,
      refreshToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
